import { Slider, type SxProps, type Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import type { KeyboardEvent } from "react";
import { useThemeContext } from "../../../globalContext/global-context";
import type { ThemeV2 } from "../../../util/types";
import ActionButton from "../ActionButton";
import { sliderStyle } from "./DiscreteScaleSlider.styles";

type DiscreteScaleSliderProps = {
  points: readonly { value: number }[];
  selectedValue: number | string | null | undefined;
  onSelect: (value: number) => void;
  testId: string;
  ariaLabel: string;
  showMarkValues?: boolean;
  disabled?: boolean;
  sx?: SystemStyleObject<Theme>;
  centerWhenUnanswered?: boolean;
};

const initialPointForKey = (
  key: string,
  points: DiscreteScaleSliderProps["points"],
) => {
  if (key === "End") return points[points.length - 1]?.value;
  if (
    [
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "PageUp",
      "PageDown",
      "Home",
    ].includes(key)
  )
    return points[0]?.value;
  return undefined;
};

const selectInitialPoint = (
  event: KeyboardEvent,
  points: DiscreteScaleSliderProps["points"],
  onSelect: DiscreteScaleSliderProps["onSelect"],
) => {
  const value = initialPointForKey(event.key, points);
  if (value === undefined) return;
  event.preventDefault();
  event.stopPropagation();
  onSelect(value);
};

const SinglePointScale = ({
  point,
  selected,
  onSelect,
  testId,
  ariaLabel,
  disabled,
}: Omit<DiscreteScaleSliderProps, "points" | "selectedValue"> & {
  point: { value: number };
  selected: boolean;
}) => (
  <ActionButton
    testId={testId}
    ariaLabel={`${ariaLabel}: ${point.value}`}
    ariaPressed={selected}
    isSelected={selected}
    disabled={disabled}
    onClick={() => onSelect(point.value)}
  >
    {point.value}
  </ActionButton>
);

const scaleMarks = (
  points: DiscreteScaleSliderProps["points"],
  showValues: boolean,
) =>
  points.map((point, index) => ({
    value: index,
    ...(showValues ? { label: String(point.value) } : {}),
  }));

const scaleStyle = (
  theme: ThemeV2,
  displayIndex: number,
  selected: boolean,
  sx?: SystemStyleObject<Theme>,
): SxProps =>
  [
    sliderStyle(theme, false),
    {
      "& .MuiSlider-mark": { transform: "translate(-50%, -50%)" },
      "& .MuiSlider-thumb": {
        marginLeft: 0,
        opacity: displayIndex < 0 ? 0 : 1,
      },
      "& .MuiSlider-thumb:focus-visible": { opacity: 1 },
      ...(!selected
        ? {
            "& .MuiSlider-track": { visibility: "hidden" },
            "& .MuiSlider-mark.MuiSlider-markActive": {
              backgroundColor: theme.tokens.color.surface.card,
            },
          }
        : {}),
    },
    sx ?? {},
  ] as SxProps;

export const DiscreteScaleSlider = ({
  points,
  selectedValue,
  onSelect,
  testId,
  ariaLabel,
  showMarkValues = false,
  disabled,
  sx,
  centerWhenUnanswered = false,
}: DiscreteScaleSliderProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const selectedIndex = points.findIndex(
    (point) => point.value === selectedValue,
  );
  const displayIndex =
    selectedIndex < 0 && centerWhenUnanswered
      ? Math.floor((points.length - 1) / 2)
      : selectedIndex;

  if (points.length === 1) {
    return (
      <SinglePointScale
        point={points[0]}
        selected={selectedIndex === 0}
        onSelect={onSelect}
        testId={testId}
        ariaLabel={ariaLabel}
        disabled={disabled}
      />
    );
  }

  return (
    <Slider
      data-testid={testId}
      aria-label={ariaLabel}
      aria-valuetext={
        selectedIndex < 0 ? theme.copy.labels.emptyRating : undefined
      }
      min={0}
      max={points.length - 1}
      step={1}
      marks={scaleMarks(points, showMarkValues)}
      value={displayIndex}
      disabled={disabled}
      onKeyDownCapture={(event) => {
        if (selectedIndex < 0 && !disabled)
          selectInitialPoint(event, points, onSelect);
      }}
      onChange={(_, index) => {
        if (typeof index === "number") onSelect(points[index].value);
      }}
      sx={scaleStyle(theme, displayIndex, selectedIndex >= 0, sx)}
    />
  );
};
